<template>
	<header ref="headerRef" class="relative z-50 w-full bg-white overflow-visible border-b border-bg-dj-grey-light">
		<ResponsiveContainer>
			<div data-header-row class="relative flex items-center justify-between gap-3 h-[75px] px-2 flex-nowrap">
				<NuxtLink data-header-logo class="md:max-w-[235px] mx-2 shrink-0" :to="`/${$i18n.locale}`">
					<DjLogo v-if="region != ''" :region="region" />
				</NuxtLink>
				<div v-show="showDesktopNav" class="hidden md:block flex-1 min-w-0" aria-hidden="true"></div>
				<div
					data-desktop-nav
					:aria-hidden="!showDesktopNav"
					class="hidden md:flex items-center shrink-0"
					:class="showDesktopNav ? '' : 'invisible absolute pointer-events-none w-max left-0 top-0'"
				>
					<div data-desktop-nav-content class="flex items-center shrink-0">
						<nav class="flex items-center shrink-0 whitespace-nowrap">
							<NuxtLink
								class="capitalize md:px-[0.8rem] md:py-3 lg:px-[0.8rem] lg:py-5 md:text-sm lg:text-base transition-colors"
								:to="`/${$i18n.locale}/jobs`"
								:class="[
									isActiveMenuItem('jobs') ? `text-dj-${buttonColor}` : '',
									`hover:text-dj-${buttonColor}`,
								]"
							>
								{{ $t("navmenu.jobAds") }}
							</NuxtLink>
							<NuxtLink
								class="capitalize md:px-[0.8rem] md:py-3 lg:px-[0.8rem] lg:py-5 md:text-sm lg:text-base transition-colors"
								:to="`/${$i18n.locale}/companies`"
								:class="[
									isActiveMenuItem('companies') ? `text-dj-${buttonColor}` : '',
									`hover:text-dj-${buttonColor}`,
								]"
							>
								{{ $t("navmenu.companies") }}
							</NuxtLink>
							<NuxtLink
								class="capitalize md:px-[0.8rem] md:py-3 lg:px-[0.8rem] lg:py-5 md:text-sm lg:text-base transition-colors"
								:to="`/${$i18n.locale}/blog`"
								:class="[
									isActiveMenuItem('blog') ? `text-dj-${buttonColor}` : '',
									`hover:text-dj-${buttonColor}`,
								]"
							>
								{{ $t("navmenu.magazine") }}
							</NuxtLink>
							<NuxtLink
								v-if="isPodcastEnabled"
								class="capitalize md:px-[0.8rem] md:py-3 lg:px-[0.8rem] lg:py-5 md:text-sm lg:text-base transition-colors"
								:to="`/${$i18n.locale}/podcasts`"
								:class="[
									isActiveMenuItem('podcasts') ? `text-dj-${buttonColor}` : '',
									`hover:text-dj-${buttonColor}`,
								]"
							>
								{{ $t("navmenu.podcast") }}
							</NuxtLink>
							<NuxtLink
								v-if="isAuth && !globalConfig.isBlue"
								class="relative capitalize md:p-3 lg:p-5 md:text-sm lg:text-base hover:text-dj-red transition-colors"
								:class="isActiveMenuItem('cv-generator') ? `text-dj-${buttonColor}` : ''"
								:to="`/${$i18n.locale}/cv-generator`"
							>
								<span
									class="absolute -top-0.5 left-1/2 -translate-x-1/2 md:left-auto md:right-1 md:translate-x-0 rounded bg-dj-red px-1 py-px text-[9px] font-bold leading-none tracking-wide text-white uppercase"
								>
									{{ $t("navmenu.cv_generator_badge") }}
								</span>
								{{ $t("navmenu.cv_generator") }}
							</NuxtLink>
							<a
								v-if="region === 'HU'"
								class="capitalize md:p-3 lg:p-5 md:text-sm lg:text-base hover:text-dj-red transition-colors"
								href="https://diakmunka.dreamjobs.hu/"
								target="_blank"
							>
								{{ $t("navmenu.diakmunka") }}
							</a>
							<div class="relative shrink-0 flex items-center" v-if="!isAuth">
								<button
									type="button"
									class="flex items-center capitalize whitespace-nowrap md:px-[0.8rem] md:py-3 lg:px-[0.8rem] lg:py-5 md:text-sm lg:text-base hover:text-dj-black transition-colors"
									:class="`text-dj-${buttonColor}`"
									@click="showEmployersDropdown = true"
								>
									{{ $t("forEmployers") }}
									<span class="material-icons-outlined text-lg">expand_more</span>
								</button>
								<BaseDropdown v-model="showEmployersDropdown" v-if="showEmployersDropdown">
									<template v-if="region !== 'HU'">
										<BaseLink
											type="grey"
											class="mb-5"
											:href="`${dashboardUrl}/${$i18n.locale}/business`"
											target="_blank"
										>
											{{ $t("navmenu.employers.forEmployers") }}
										</BaseLink>
									</template>
									<NuxtLink
										v-if="isInsideVuePricesEnabled"
										type="grey"
										class="mb-5"
										:to="`/${$i18n.locale}/business/prices`"
									>
										{{ $t("navmenu.employers.prices") }}
									</NuxtLink>
									<BaseLink
										v-else
										type="grey"
										class="mb-5"
										:href="`${dashboardUrl}/${$i18n.locale}/business#prices`"
										target="_blank"
									>
										{{ $t("navmenu.employers.prices") }}
									</BaseLink>
									<template v-if="region !== 'HU'">
										<BaseLink
											type="grey"
											class="mb-5"
											:href="premiumRecruitmentUrl"
											target="_blank"
										>
											{{ $t("navmenu.employers.premium-toborzas") }}
										</BaseLink>
										<BaseLink
											v-if="szmdStarted"
											type="grey"
											class="mb-5"
											:href="szmdUrl"
											target="_blank"
										>
											{{ $t("szmdWinner") }}
										</BaseLink>
									</template>
									<BaseLink
										class="w-full mb-3"
										:href="`${dashboardUrl}/${$i18n.locale}`"
										target="_blank"
									>
										<BaseButton type="tertiary" class="w-full">
											{{ $t("navmenu.employers.loginEmployer") }}
										</BaseButton>
									</BaseLink>
									<BaseLink
										class="w-full mb-3"
										:href="`${dashboardUrl}/${$i18n.locale}/register`"
										target="_blank"
									>
										<BaseButton v-if="globalConfig.isBlue" type="primary" class="w-full">
											{{ $t("navmenu.employers.registrationEmployer") }}
										</BaseButton>
										<BaseButton v-else type="primary-red" class="w-full">
											{{ $t("navmenu.employers.registrationEmployer") }}
										</BaseButton>
									</BaseLink>
								</BaseDropdown>
							</div>
						</nav>
						<div data-locale-panel class="ml-3 mr-2 shrink-0">
							<BaseLocaleChanger
								icon
								v-model="locale"
								:locales="locales"
								@onLocaleChange="handleLocaleChange"
							/>
						</div>
					</div>
				</div>
				<div v-show="showDesktopNav" class="hidden md:block flex-1 min-w-0" aria-hidden="true"></div>
				<div
					data-header-actions
					:aria-hidden="!showDesktopNav"
					class="items-center shrink-0"
					:class="
						showDesktopNav
							? 'flex justify-end'
							: 'hidden md:flex invisible absolute pointer-events-none w-max'
					"
				>
					<template v-if="!isAuth">
						<BaseButton
							@click="$router.push(`/${$i18n.locale}/login`)"
							type="tertiary"
							class="hidden md:flex capitalize ml-6"
						>
							{{ $t("loginBtn") }}
						</BaseButton>
						<BaseButton
							@click="$router.push(`/${$i18n.locale}/registration`)"
							:subtitle="$t('forUsers')"
							class="hidden md:flex capitalize ml-4"
						>
							{{ $t("registerBtn") }}
						</BaseButton>
					</template>
					<UserMenu class="ml-6" v-else />
				</div>
				<div
					class="flex items-center shrink-0 whitespace-nowrap gap-1"
					:class="showDesktopNav ? 'md:hidden' : ''"
				>
					<BaseLocaleChanger
						icon
						class="shrink-0"
						v-model="locale"
						:locales="locales"
						@onLocaleChange="handleLocaleChange"
					/>
					<BaseLink @click="showMobileMenuMain = true" type="grey" class="p-3">
						<span class="material-icons-outlined">menu</span>
					</BaseLink>
				</div>
				<BaseMobileNav
					:openItem="showMobileMenuMain"
					@closeAll="showMobileMenuMain = false"
					:class="{ 'md:hidden': showDesktopNav }"
				>
					<ul>
						<li class="flex w-full justify-start pt-1 pb-8 pl-1">
							<DjLogo v-if="region != ''" :region="region" class="w-auto" />
						</li>
						<li class="flex justify-between w-full py-4">
							<NuxtLink class="capitalize font-medium text-2xl" :to="`/${$i18n.locale}/jobs`">
								{{ $t("navmenu.jobAds") }}
							</NuxtLink>
						</li>
						<li class="flex justify-between w-full py-4">
							<NuxtLink class="capitalize font-medium text-2xl" :to="`/${$i18n.locale}/companies`">
								{{ $t("navmenu.companies") }}
							</NuxtLink>
						</li>
						<li class="flex justify-between w-full py-4">
							<NuxtLink class="capitalize font-medium text-2xl" :to="`/${$i18n.locale}/blog`">
								{{ $t("navmenu.magazine") }}
							</NuxtLink>
						</li>

						<li v-if="isPodcastEnabled" class="flex justify-between w-full py-4">
							<NuxtLink class="capitalize font-medium text-2xl" :to="`/${$i18n.locale}/podcasts`">
								{{ $t("navmenu.podcast") }}
							</NuxtLink>
						</li>

						<li class="relative flex justify-between w-full py-4" v-if="isAuth && !globalConfig.isBlue">
							<NuxtLink
								class="relative capitalize font-medium text-2xl"
								:to="`/${$i18n.locale}/cv-generator`"
							>
								<span
									class="absolute -top-3 left-0 rounded bg-dj-red px-1.5 py-0.5 text-[10px] font-bold leading-none tracking-wide text-white uppercase"
								>
									{{ $t("navmenu.cv_generator_badge") }}
								</span>
								{{ $t("navmenu.cv_generator") }}
							</NuxtLink>
						</li>
						<li v-if="region === 'HU'" class="flex justify-between w-full py-4">
							<a
								class="capitalize font-medium text-2xl"
								href="https://diakmunka.dreamjobs.hu/"
								target="_blank"
							>
								{{ $t("navmenu.diakmunka") }}
							</a>
						</li>
						<!-- New version
                    <li class="flex justify-between w-full py-4" v-if="isAuth && !globalConfig.isBlue">
                        <NuxtLink
                            class="capitalize font-medium text-2xl"
                            :to="`/${$i18n.locale}/user/${useAuth.getUser()?.slug}/profile`"
                        >
                            {{ $t("navmenu.sbp") }}
                        </NuxtLink>
                    </li> -->

						<li v-if="region === 'RO'" class="flex justify-between w-full py-4">
							<NuxtLink class="capitalize font-medium text-2xl" :to="`/${locale}/junior-jobs`">
								{{ $t("landings.junior-jobs.submenu") }}
							</NuxtLink>
						</li>
						<li class="flex justify-between w-full py-4">
							<NuxtLink
								class="capitalize font-medium text-2xl"
								:to="isAuth ? `/${locale}/user/notifications` : `/${locale}/jobnotification`"
							>
								{{ $t("user.notifications.jobsShort") }}
							</NuxtLink>
						</li>
						<li v-if="region === 'HU' || region === 'RO'" class="flex justify-between w-full py-4">
							<NuxtLink class="capitalize font-medium text-2xl" :to="salaryCalculatorUrl">
								{{ $t("berkalkulator.shortTitle") }}
							</NuxtLink>
						</li>
						<li v-if="globalConfig.isNotBlueHU && region === 'HU'" class="flex justify-between w-full py-4">
							<NuxtLink class="capitalize font-medium text-2xl" :to="`/${$i18n.locale}/fizetesek`">
								Fizetések
							</NuxtLink>
						</li>
						<li class="flex justify-between w-full py-4" v-if="szmdVotingStatus">
							<NuxtLink class="capitalize font-medium text-2xl" @click="openSzmdBoxVoteModal()">
								<span class="text-dj-red">
									{{
										$i18n.locale === "ro"
											? $t("voting") + " " + $t("szmdTexts.szmdTitle")
											: $t("szmdTexts.szmdTitle") + " " + $t("voting")
									}}
									&raquo;
								</span>
							</NuxtLink>
						</li>
						<li
							v-if="!isAuth"
							@click="showMobileMenuForEmployers = true"
							class="flex justify-between w-full pt-2 pb-6"
						>
							<BaseButton
								class="flex justify-between w-full capitalize font-medium text-2xl transition-colors max-w-[280px]"
								type="secondary-red"
								size="normal"
								@click="showEmployersDropdown = true"
							>
								{{ $t("forEmployers") }}
								<span class="material-icons-outlined text-2xl">chevron_right</span>
							</BaseButton>
							<!-- <div class="capitalize font-medium text-2xl">{{ $t("forEmployers") }}</div>
                        <div>
                            <span class="material-icons-outlined text-2xl">chevron_right</span>
                        </div> -->
						</li>
						<li
							v-if="isAuth"
							@click="showMobileMenuProfile = true"
							class="flex justify-between w-full py-4"
						>
							<div class="capitalize font-medium text-2xl">{{ $t("myProfile") }}</div>
							<div>
								<span class="material-icons-outlined text-2xl">chevron_right</span>
							</div>
						</li>
						<template v-if="!isAuth">
							<li class="flex justify-start w-full mt-4 py-4">
								<BaseButton
									@click="$router.push(`/${$i18n.locale}/login`)"
									type="tertiary"
									class="capitalize w-full max-w-[335px]"
								>
									{{ $t("loginBtn") }}
								</BaseButton>
							</li>
							<li class="flex justify-start w-full py-4">
								<BaseButton
									@click="$router.push(`/${$i18n.locale}/registration`)"
									:subtitle="$t('forUsers')"
									class="capitalize w-full max-w-[335px]"
								>
									{{ $t("registerBtn") }}
								</BaseButton>
							</li>
						</template>
						<template v-else>
							<li class="flex justify-start items-center w-full py-4" @click="logout">
								<BaseIcon color="#E1415A" class="mr-4 text-[24px]" icon="mdi:location-exit" />
								<span class="text-dj-red text-lg">{{ $t("userMenu.logout") }}</span>
							</li>
						</template>
						<li class="flex justify-start w-full py-4">
							<div class="w-full max-w-[335px] relative">
								<BaseLocaleChanger
									class="mr-2"
									v-model="locale"
									:locales="locales"
									@onLocaleChange="handleLocaleChange"
								/>
							</div>
						</li>
					</ul>
				</BaseMobileNav>
				<BaseMobileNav
					:openItem="showMobileMenuForEmployers"
					@closeAll="
						showMobileMenuMain = false;
						showMobileMenuForEmployers = false;
					"
					:class="{ 'md:hidden': showDesktopNav }"
				>
					<ul>
						<li class="flex justify-between w-full py-3">
							<NuxtLink
								v-if="isInsideVuePricesEnabled"
								type="grey"
								:to="`/${$i18n.locale}/business/prices`"
							>
								{{ $t("navmenu.employers.prices") }}
							</NuxtLink>
							<BaseLink
								v-else
								type="grey"
								:href="`${dashboardUrl}/${$i18n.locale}/business#prices`"
								target="_blank"
							>
								{{ $t("navmenu.employers.prices") }}
							</BaseLink>
						</li>
						<template v-if="region !== 'HU'">
							<li class="flex justify-between w-full py-3">
								<BaseLink type="grey" :href="premiumRecruitmentUrl" target="_blank">
									{{ $t("navmenu.employers.premium-toborzas") }}
								</BaseLink>
							</li>
							<li class="flex justify-between w-full py-3" v-if="szmdStarted">
								<BaseLink type="grey" class="mb-5" :href="szmdUrl" target="_blank">
									{{ $t("szmdWinner") }}
								</BaseLink>
							</li>
						</template>
						<li class="flex justify-start w-full mt-2 py-2">
							<BaseLink
								class="w-full max-w-[335px]"
								:href="`${dashboardUrl}/${$i18n.locale}`"
								target="_blank"
							>
								<BaseButton type="tertiary" class="w-full">
									{{ $t("navmenu.employers.loginEmployer") }}
								</BaseButton>
							</BaseLink>
						</li>
						<li class="flex justify-start w-full py-3">
							<BaseLink
								class="w-full max-w-[335px]"
								:href="`${dashboardUrl}/${$i18n.locale}/register`"
								target="_blank"
							>
								<BaseButton type="primary-red" class="w-full">
									{{ $t("navmenu.employers.registrationEmployer") }}
								</BaseButton>
							</BaseLink>
						</li>
					</ul>
				</BaseMobileNav>
				<BaseMobileNav
					v-if="isAuth"
					:openItem="showMobileMenuProfile"
					@closeAll="
						showMobileMenuMain = false;
						showMobileMenuProfile = false;
					"
					:class="{ 'md:hidden': showDesktopNav }"
				>
					<ul>
						<li class="flex items-center w-full pb-2">
							<BaseLink type="gray" class="relative -left-2" @click="showMobileMenuProfile = false">
								<span class="material-icons-outlined text-2xl">chevron_left</span>
							</BaseLink>
							<span class="capitalize font-medium text-xl">{{ $t("myProfile") }}</span>
						</li>
						<li class="flex items-center w-full pt-2 pb-2">
							<BaseCloudImage
								v-if="useAuth.getUser().avatar"
								class="max-w-[44px] md:max-w-[80px] mr-5 rounded-full overflow-hidden"
								:public-id="useAuth.getUser().avatar"
								alt="Avatar"
								:transformation="[{ width: 80, height: 80, radius: 40, crop: 'thumb' }]"
							/>
							<div
								v-else
								class="flex justify-center items-center bg-gray-100 rounded-full mr-5 w-[44px] md:w-[60px] h-[44px] md:h-[60px]"
							>
								<BaseIcon
									color="#969BA5"
									class="text-[24px] w-[60%] h-[60%]"
									icon="icon-park-outline:user"
								/>
							</div>

							<div class="flex flex-col justify-center h-full">
								<span class="font-medium text-sm">
									<template v-if="locale == 'hu'">
										{{ useAuth.getUser().vname }} {{ useAuth.getUser().kname }}
									</template>
									<template v-else>
										{{ useAuth.getUser().kname }} {{ useAuth.getUser().vname }}
									</template>
								</span>
								<span class="hidden md:block text-sm">{{ useAuth.getUser().email }}</span>
							</div>
						</li>
						<li class="flex justify-between w-full py-3">
							<NuxtLink class="flex items-center" :to="`/${$i18n.locale}/saved-jobs`" @click="hideMenus">
								<BaseIcon
									color="#969BA5"
									class="mr-4 text-[24px]"
									icon="material-symbols:shield-with-heart-outline-rounded"
								/>
								<span>{{ $t("user.savedJobs.title") }}</span>
							</NuxtLink>
						</li>
						<li class="flex justify-between w-full py-3">
							<NuxtLink
								class="flex items-center"
								:to="`/${$i18n.locale}/my-applications`"
								@click="hideMenus"
							>
								<BaseIcon color="#969BA5" class="mr-4 text-[24px]" icon="prime:send" />
								<span>{{ $t("userMenu.applications") }}</span>
							</NuxtLink>
						</li>
						<li class="flex justify-between w-full py-3">
							<NuxtLink
								class="flex items-center"
								:to="`/${$i18n.locale}/followed-companies`"
								@click="hideMenus"
							>
								<BaseIcon color="#969BA5" class="mr-4 text-[24px]" icon="carbon:location-company" />
								<span>{{ $t("user.followedCompanies.title") }}</span>
							</NuxtLink>
						</li>
						<li @click="showMobileMenuAccount = true" class="flex justify-between w-full py-3">
							<div class="flex items-center">
								<BaseIcon color="#969BA5" class="mr-4 text-[24px]" icon="icon-park-outline:user" />
								<span>{{ $t("userMenu.profile") }}</span>
							</div>
							<span class="material-icons-outlined text-2xl">chevron_right</span>
						</li>
					</ul>
				</BaseMobileNav>
				<BaseMobileNav
					v-if="isAuth"
					:openItem="showMobileMenuAccount"
					@closeAll="
						showMobileMenuMain = false;
						showMobileMenuProfile = false;
						showMobileMenuAccount = false;
					"
					:class="{ 'md:hidden': showDesktopNav }"
				>
					<ul>
						<li class="flex items-center w-full pb-2">
							<BaseLink type="gray" class="relative -left-2" @click="showMobileMenuAccount = false">
								<span class="material-icons-outlined text-2xl">chevron_left</span>
							</BaseLink>
							<span class="capitalize font-medium text-xl">{{ $t("user.title") }}</span>
						</li>
						<li class="flex justify-between w-full py-3 pl-2">
							<NuxtLink :to="`/${$i18n.locale}/user/account`" @click="hideMenus">
								<span class="flex self-start text-base font-medium">
									{{ $t("user.account.title") }}
								</span>
							</NuxtLink>
						</li>
						<li class="flex justify-between w-full py-3 pl-2">
							<NuxtLink :to="`/${$i18n.locale}/user/change-password`" @click="hideMenus">
								<span class="flex self-start text-base font-medium">
									{{ $t("user.changePassword.title") }}
								</span>
							</NuxtLink>
						</li>
						<li class="flex justify-between w-full py-3 pl-2">
							<NuxtLink :to="`/${$i18n.locale}/user/cv`" @click="hideMenus">
								<span class="flex self-start text-base font-medium">{{ $t("user.cvs.title") }}</span>
							</NuxtLink>
						</li>
						<li v-if="!globalConfig.isBlue" class="flex justify-between w-full py-3 pl-2">
							<NuxtLink :to="`/${$i18n.locale}/user/linkedin`" @click="hideMenus">
								<span class="flex self-start text-base font-medium">
									{{ $t("user.linkedin.title") }}
								</span>
							</NuxtLink>
						</li>
						<li class="flex justify-between w-full py-3 pl-2">
							<NuxtLink :to="`/${$i18n.locale}/user/notifications`" @click="hideMenus">
								<span class="flex self-start text-base font-medium">
									{{ $t("user.notifications.titleNavigation") }}
								</span>
							</NuxtLink>
						</li>
					</ul>
				</BaseMobileNav>
			</div>
		</ResponsiveContainer>
	</header>
	<div
		v-if="
			!isActiveMenuItem('saved-jobs') &&
			!isActiveMenuItem('my-applications') &&
			!isActiveMenuItem('followed-companies') &&
			!isActiveMenuItem('user')
		"
		class="relative z-40 w-full border-b border-bg-grey-light"
	>
		<ResponsiveContainer class="hidden md:flex justify-center">
			<ul class="flex">
				<li v-if="region === 'RO'" class="whitespace-nowrap float-left">
					<NuxtLink :to="`/${locale}/junior-jobs`">
						<BaseLink :type="String([isActiveMenuItem('junior-jobs') ? 'nav-active' : 'nav'])" class="py-4">
							<span class="text-sm">{{ $t("landings.junior-jobs.submenu") }}</span>
						</BaseLink>
					</NuxtLink>
				</li>
				<li class="whitespace-nowrap float-left">
					<NuxtLink :to="isAuth ? `/${locale}/user/notifications` : `/${locale}/jobnotification`">
						<BaseLink
							:type="String([isActiveMenuItem('jobnotification') ? 'nav-active' : 'nav'])"
							class="py-4"
						>
							<span class="text-sm">{{ $t("user.notifications.jobsShort") }}</span>
						</BaseLink>
					</NuxtLink>
				</li>
				<li class="whitespace-nowrap float-left" v-if="region === 'HU' || region === 'RO'">
					<NuxtLink :to="salaryCalculatorUrl">
						<BaseLink :type="String([isSalaryCalculatorActive() ? 'nav-active' : 'nav'])" class="py-4">
							<span class="text-sm">{{ $t("berkalkulator.shortTitle") }}</span>
						</BaseLink>
					</NuxtLink>
				</li>
				<li class="whitespace-nowrap float-left" v-if="globalConfig.isNotBlueHU && region === 'HU'">
					<NuxtLink :to="`/${locale}/fizetesek`">
						<BaseLink :type="String([isActiveMenuItem('fizetesek') ? 'nav-active' : 'nav'])" class="py-4">
							<span class="text-sm">Fizetések</span>
						</BaseLink>
					</NuxtLink>
				</li>
				<li class="whitespace-nowrap float-left" v-if="szmdVotingStatus">
					<BaseLink type="nav" class="py-4" href="#" @click="openSzmdBoxVoteModal()">
						<span class="text-sm text-dj-red">
							{{
								$i18n.locale === "ro"
									? $t("voting") + " " + $t("szmdTexts.szmdTitle")
									: $t("szmdTexts.szmdTitle") + " " + $t("voting")
							}}
							&raquo;
						</span>
					</BaseLink>
				</li>
			</ul>
		</ResponsiveContainer>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, inject, nextTick, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import useAuth from "~~/composables/useAuth";
import { ConfigRepository } from "~~/repositories/ConfigRepository";
import { SALARY_CALCULATOR_SLUGS } from "~/composables/useSalaryCalculatorRoute";

export default defineComponent({
	name: "StickyHeader",
	setup() {
		const { locale } = useI18n();
		const configRepo = new ConfigRepository();

		const router = useRouter();
		const route = useRoute();

		const isAuth = useState("isAuth");
		const headerRef = ref<HTMLElement | null>(null);
		const showDesktopNav = ref(true);
		const resizeObserver = ref<ResizeObserver | null>(null);
		const region = ref("");

		const showEmployersDropdown = ref(false);
		const showMobileMenuMain = ref(false);
		const showMobileMenuForEmployers = ref(false);
		const showMobileMenuProfile = ref(false);
		const showMobileMenuAccount = ref(false);

		const dashboardUrl = ref("");
		const szmdUrl = ref("");
		const premiumRecruitmentUrl = ref("");
		const salaryCalculatorUrl = ref("");

		const config = useRuntimeConfig();
		const szmdEntryStartAt = new Date(config.public.activeConfig.szmdConfig.entryStartAt).getTime();
		const szmdStarted = szmdEntryStartAt < Date.now();

		const szmdVotingStatus = useSzmdVotingStatus();
		const buttonColor = ref("red");

		const isInsideVuePricesEnabled = computed(() => {
			return config.public.activeFeatures.includes("use_inside_vue_prices");
		});

		const isPodcastEnabled = computed(() => {
			const regions = config.public.podcastRegions as string[];
			return regions.includes(region.value.toLowerCase());
		});

		const openSzmdBoxVoteModal = inject("openSzmdBoxVoteModal");

		function outerWidth(el: HTMLElement) {
			const style = window.getComputedStyle(el);
			return el.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
		}

		function updateDesktopNavVisibility() {
			const headerEl = headerRef.value;
			if (!headerEl) return;

			if (window.innerWidth < 768) {
				showDesktopNav.value = true;
				return;
			}

			const rowEl = headerEl.querySelector<HTMLElement>("[data-header-row]");
			const logoEl = headerEl.querySelector<HTMLElement>("[data-header-logo]");
			const navContentEl = headerEl.querySelector<HTMLElement>("[data-desktop-nav-content]");
			const actionsEl = headerEl.querySelector<HTMLElement>("[data-header-actions]");

			if (!rowEl || !logoEl || !navContentEl || !actionsEl) {
				showDesktopNav.value = true;
				return;
			}

			const rowStyle = window.getComputedStyle(rowEl);
			const horizontalPadding = parseFloat(rowStyle.paddingLeft) + parseFloat(rowStyle.paddingRight);
			const availableWidth = rowEl.clientWidth - horizontalPadding;
			const gap = parseFloat(rowStyle.columnGap || rowStyle.gap) || 12;
			const requiredWidth = outerWidth(logoEl) + outerWidth(navContentEl) + outerWidth(actionsEl) + gap * 2;

			if (showDesktopNav.value) {
				showDesktopNav.value = requiredWidth <= availableWidth;
			} else {
				showDesktopNav.value = requiredWidth + 16 <= availableWidth;
			}

			if (!showDesktopNav.value) {
				showEmployersDropdown.value = false;
			}
		}

		watch(router.currentRoute, () => {
			// Hide mobile navigation on url change
			hideMenus();
		});

		function hideMenus() {
			showEmployersDropdown.value = false;
			showMobileMenuMain.value = false;
			showMobileMenuForEmployers.value = false;
			showMobileMenuProfile.value = false;
			showMobileMenuAccount.value = false;
		}

		const locales = ref<string[]>([]);

		function logout() {
			useAuth.logout();
		}

		function getLocalizedRouteName(targetLocale: string): string | symbol | undefined {
			if (typeof route.name !== "string") {
				return route.name ?? undefined;
			}

			if (route.name.includes("___")) {
				return route.name.replace(/___[a-z]{2}$/i, `___${targetLocale}`);
			}

			return route.name;
		}

		function isLocalizedJobSlugRoute(routeName: string) {
			return routeName.includes("job-company_slug-slug") || routeName.includes("apply-company_slug-slug");
		}

		function resolveLocalizedJobPath(targetLocale: string): string | null {
			const routeName = String(route.name ?? "");
			if (!isLocalizedJobSlugRoute(routeName)) {
				return null;
			}

			const currentSlug = Array.isArray(route.params.slug) ? route.params.slug[0] : route.params.slug;
			if (!currentSlug) {
				return null;
			}

			const payloadData = useNuxtApp().payload.data as Record<string, unknown>;
			let localizedSlug = "";

			for (const value of Object.values(payloadData)) {
				if (!value || typeof value !== "object") {
					continue;
				}

				const item = value as { slug?: Record<string, string> };
				const translatedSlug = item.slug?.[targetLocale];
				if (item.slug && translatedSlug && Object.values(item.slug).includes(currentSlug)) {
					localizedSlug = translatedSlug;
					break;
				}
			}

			if (!localizedSlug) {
				return null;
			}

			const localizedRouteName = getLocalizedRouteName(targetLocale);
			if (!localizedRouteName) {
				return null;
			}

			const resolvedRoute = router.resolve({
				name: localizedRouteName,
				params: {
					...route.params,
					slug: localizedSlug,
				},
				query: route.query,
				hash: route.hash,
			});

			return resolvedRoute.fullPath;
		}

		function handleLocaleChange() {
			const targetLocale = locale.value;

			// 1. Get the actual current locale straight from the URL before navigation
			let currentUrlLocale = route.path.split("/").filter(Boolean)[0];
			if (typeof route.name === "string") {
				const match = route.name.match(/___([a-z]{2})$/i);
				if (match) {
					currentUrlLocale = match[1];
				}
			}

			const routeName = String(route.name ?? "");
			if (isLocalizedJobSlugRoute(routeName)) {
				const localizedJobPath = resolveLocalizedJobPath(targetLocale);
				if (localizedJobPath) {
					router.push(localizedJobPath).then(() => {
						if (useAuth.getUser()) {
							useAuth.updateUser();
						}
					});
					return;
				}

				// Job pages stay on the current language when that job has no slug for the
				// selected language. Apply pages still switch, then correct the slug.
				if (routeName.includes("job-company_slug-slug")) {
					nextTick(() => {
						locale.value = currentUrlLocale;
					});
					return;
				}
			}

			const localizedRouteName = getLocalizedRouteName(targetLocale);
			const localizedResolvedRoute =
				localizedRouteName == null
					? null
					: router.resolve({
							name: localizedRouteName,
							params: route.params,
							query: route.query,
							hash: route.hash,
					  });

			const nextPath = localizedResolvedRoute?.fullPath || route.fullPath;

			router.push(nextPath).then(() => {
				if (useAuth.getUser()) {
					useAuth.updateUser(); // Update user data after changing locale
				}
			});
		}

		onMounted(() => {
			if (globalConfig.isBlue) buttonColor.value = "blue";
			const config = configRepo.getCurrentConfig();
			locales.value = config.availableLangs;
			region.value = config.code;

			updateConfig();

			resizeObserver.value = new ResizeObserver(() => {
				updateDesktopNavVisibility();
			});

			if (headerRef.value) {
				resizeObserver.value.observe(headerRef.value);
				const rowEl = headerRef.value.querySelector("[data-header-row]");
				if (rowEl) {
					resizeObserver.value.observe(rowEl);
				}
			}

			window.addEventListener("resize", updateDesktopNavVisibility);
			nextTick(updateDesktopNavVisibility);
		});

		onBeforeUnmount(() => {
			window.removeEventListener("resize", updateDesktopNavVisibility);
			if (resizeObserver.value) {
				resizeObserver.value.disconnect();
				resizeObserver.value = null;
			}
		});

		function updateConfig() {
			const config = configRepo.getCurrentConfig();

			dashboardUrl.value = config.dashUrl;
			szmdUrl.value = configRepo.getSzmdUrl(locale.value);
			premiumRecruitmentUrl.value = configRepo.getPremiumRecruitmentUrl(locale.value);
			salaryCalculatorUrl.value = configRepo.getSalaryCalculatorUrl(locale.value);
		}

		watch([locale, isAuth], () => {
			updateConfig();
			nextTick(updateDesktopNavVisibility);
		});

		watch([region, locales], () => {
			nextTick(updateDesktopNavVisibility);
		});

		function isActiveMenuItem(val: string) {
			return router.currentRoute.value.path.indexOf(String(val)) > -1;
		}

		function isSalaryCalculatorActive() {
			return SALARY_CALCULATOR_SLUGS.some((slug) => isActiveMenuItem(slug));
		}

		return {
			locale,
			headerRef,
			showDesktopNav,
			isAuth,
			hideMenus,
			logout,
			showEmployersDropdown,
			showMobileMenuMain,
			showMobileMenuForEmployers,
			showMobileMenuProfile,
			showMobileMenuAccount,
			locales,
			handleLocaleChange,
			region,
			dashboardUrl,
			isActiveMenuItem,
			szmdStarted,
			szmdUrl,
			premiumRecruitmentUrl,
			salaryCalculatorUrl,
			isSalaryCalculatorActive,
			szmdVotingStatus,
			buttonColor,
			openSzmdBoxVoteModal,
			isInsideVuePricesEnabled,
			isPodcastEnabled,
		};
	},
});
</script>
